import { BrowserCacheLocation, createNestablePublicClientApplication } from '@azure/msal-browser';
import type { AuthenticationResult } from '@azure/msal-browser';
import { environment } from '@env';
import type { IOutlookSelectionSnapshot, OutlookHost } from './office-hosts/outlook-host';
import { parseOutlookMessageId, resolveOutlookConversationIdentity } from './outlook-message-headers';

const GRAPH_ORIGIN = 'https://graph.microsoft.com';
const GRAPH_MESSAGES_PATH = '/v1.0/me/messages';
const PAGE_SIZE = 10;
const MAX_PAGES = 5;
const MAX_ITEMS = PAGE_SIZE * MAX_PAGES;
const ROOT_CONVERSATION_INDEX_BYTES = 22;
const REPLY_CONVERSATION_INDEX_BYTES = 5;
const ID_HEADERS = new Set([ 'message-id', 'references', 'in-reply-to' ]);
interface IGraphToken
{
    readonly accessToken: string;
    readonly username: string;
    readonly verifiedOfficeIdentity?: boolean;
    readonly verifiedGraphItem?: IGraphMessage;
}

interface IGraphMessage
{
    readonly id: string;
    readonly internetMessageId: string;
    readonly conversationId: string;
    readonly conversationIndex?: string | null;
    readonly receivedDateTime: string;
}

interface IGraphHeader
{
    readonly name: string;
    readonly value: string;
}

function isOutlookComMailbox(): boolean
{
    return typeof Office !== 'undefined' && !!Office
        && Office.context?.mailbox?.userProfile?.accountType === 'outlookCom';
}

function isOfficeIdentityMailbox(): boolean
{
    const accountType = typeof Office !== 'undefined' && !!Office
        ? Office.context?.mailbox?.userProfile?.accountType : null;
    return accountType === 'outlookCom' || accountType === 'office365';
}

export async function acquireOutlookGraphToken(
    mailboxHint: string,
    isActive: () => boolean = () => true,
    createApplication: typeof createNestablePublicClientApplication = createNestablePublicClientApplication,
    interactive = false,
    getAuthContext: () => Promise<Office.AuthContext> = () => Office.auth.getAuthContext(),
    verifySelectedItem?: (accessToken: string) => Promise<IGraphMessage | null>,
): Promise<IGraphToken | null>
{
    if (!isActive())
    {
        return null;
    }
    let authContext: Office.AuthContext | null = null;
    try
    {
        authContext = await getAuthContext();
    }
    catch
    {
        authContext = null;
    }
    if (!isActive())
    {
        return null;
    }
    let app: Awaited<ReturnType<typeof createApplication>>;
    try
    {
        app = await createApplication({
            auth: {
                clientId: environment.msal_client_id,
                authority: environment.msal_authority,
                redirectUri: `${ location.origin }${ environment.msal_dialog_path }`,
                supportsNestedAppAuth: true,
            },
            cache: {
                cacheLocation: BrowserCacheLocation.MemoryStorage,
                temporaryCacheLocation: BrowserCacheLocation.MemoryStorage,
            },
        });
    }
    catch
    {
        return null;
    }
    if (!isActive())
    {
        return null;
    }
    const request = { scopes: [ 'Mail.ReadBasic' ], loginHint: authContext?.loginHint?.trim() || mailboxHint };
    let result: AuthenticationResult;
    try
    {
        result = interactive
            ? await app.acquireTokenPopup({ ...request, prompt: 'select_account' })
            : await app.ssoSilent(request);
    }
    catch
    {
        return null;
    }
    if (!isActive())
    {
        return null;
    }
    if (!result.accessToken || !result.account?.username?.trim())
    {
        return null;
    }
    const claims = result.account.idTokenClaims;
    const officeObjectId = authContext?.userObjectId;
    const officeTenantId = authContext?.tenantId;
    const objectClaimMatches = !!officeObjectId && !!claims?.oid
        && officeObjectId.toLowerCase() === claims.oid.toLowerCase();
    const tenantClaimMatches = !!officeTenantId && !!claims?.tid
        && officeTenantId.toLowerCase() === claims.tid.toLowerCase();
    const verifiedOfficeIdentity = isOfficeIdentityMailbox() && !!officeObjectId?.trim() && !!officeTenantId?.trim()
        && !!claims?.oid?.trim() && !!claims?.tid?.trim() && objectClaimMatches && tenantClaimMatches;
    const sameEmail = result.account.username.toLowerCase() === mailboxHint.toLowerCase();
    const identityMismatch = (officeObjectId && claims?.oid && !objectClaimMatches)
        || (officeTenantId && claims?.tid && !tenantClaimMatches);
    if (identityMismatch || (!sameEmail && !verifiedOfficeIdentity))
    {
        if (isOutlookComMailbox() && verifySelectedItem)
        {
            try
            {
                const verifiedGraphItem = await verifySelectedItem(result.accessToken);
                if (!isActive())
                {
                    return null;
                }
                if (verifiedGraphItem && isGraphMessage(verifiedGraphItem))
                {
                    return { accessToken: result.accessToken, username: result.account.username, verifiedGraphItem };
                }
            }
            catch
            {
                return null;
            }
        }
        return null;
    }
    return verifiedOfficeIdentity
        ? { accessToken: result.accessToken, username: result.account.username, verifiedOfficeIdentity: true }
        : { accessToken: result.accessToken, username: result.account.username };
}

function graphMessagesUrl(value: string): URL | null
{
    try
    {
        const url = new URL(value);
        return url.origin === GRAPH_ORIGIN && url.pathname === GRAPH_MESSAGES_PATH && !url.username && !url.password
            ? url : null;
    }
    catch
    {
        return null;
    }
}

async function graphGet(url: URL, token: string): Promise<unknown>
{
    const response = await fetch(url.toString(), { headers: { Authorization: `Bearer ${ token }` } });
    if (!response.ok)
    {
        throw new Error('Graph request failed');
    }
    return response.json();
}

function conversationIndexLength(value: unknown): number | null
{
    if (typeof value !== 'string' || !/^[A-Za-z0-9+/]+={0,2}$/.test(value))
    {
        return null;
    }
    try
    {
        const bytes = atob(value);
        return btoa(bytes) === value && bytes.length >= ROOT_CONVERSATION_INDEX_BYTES
            && (bytes.length - ROOT_CONVERSATION_INDEX_BYTES) % REPLY_CONVERSATION_INDEX_BYTES === 0
            ? bytes.length : null;
    }
    catch
    {
        return null;
    }
}

function isGraphMessage(value: unknown): value is IGraphMessage
{
    if (!value || typeof value !== 'object')
    {
        return false;
    }
    const message = value as Partial<IGraphMessage>;
    return typeof message.id === 'string' && !!message.id && message.id !== '.' && message.id !== '..'
        && message.id.length <= 2_048 && !!parseOutlookMessageId(message.internetMessageId)
        && typeof message.conversationId === 'string' && !!message.conversationId
        && (message.conversationIndex == null || typeof message.conversationIndex === 'string')
        && typeof message.receivedDateTime === 'string' && Number.isFinite(Date.parse(message.receivedDateTime));
}

async function listMessages(
    filter: string,
    token: string,
    host: OutlookHost,
    snapshot: IOutlookSelectionSnapshot,
    isActive: () => boolean,
): Promise<IGraphMessage[] | null>
{
    const first = new URL(GRAPH_MESSAGES_PATH, GRAPH_ORIGIN);
    first.searchParams.set('$select', 'id,internetMessageId,conversationId,conversationIndex,receivedDateTime');
    first.searchParams.set('$filter', filter);
    first.searchParams.set('$top', String(PAGE_SIZE));
    const messages: IGraphMessage[] = [];
    let next: URL | null = first;
    for (let page = 0; next && page < MAX_PAGES; page++)
    {
        if (!isActive() || !host.isSelectionCurrent(snapshot))
        {
            return null;
        }
        const response = await graphGet(next, token) as { value?: unknown; '@odata.nextLink'?: unknown } | null;
        if (!isActive() || !host.isSelectionCurrent(snapshot) || !response || !Array.isArray(response.value)
            || messages.length + response.value.length > MAX_ITEMS || response.value.some((row) => !isGraphMessage(row)))
        {
            return null;
        }
        messages.push(...response.value);
        const link = response[ '@odata.nextLink' ];
        next = link === undefined ? null : typeof link === 'string' ? graphMessagesUrl(link) : null;
        if (link !== undefined && !next)
        {
            return null;
        }
    }
    return next ? null : messages;
}

export async function resolveOutlookGraphGroupRoot(
    host: OutlookHost,
    snapshot: IOutlookSelectionSnapshot,
    mailboxHint: string,
    acquireToken: (mailboxHint: string, isActive: () => boolean,
        verifySelectedItem: (accessToken: string) => Promise<IGraphMessage | null>) => Promise<IGraphToken | null>
        = (hint, active, verifySelectedItem) => acquireOutlookGraphToken(hint, active, undefined, false, undefined, verifySelectedItem),
    isActive: () => boolean = () => true,
): Promise<string | null>
{
    const selectedId = parseOutlookMessageId(snapshot.isCompose === true ? snapshot.inReplyTo : snapshot.internetMessageId);
    if (!snapshot.hasItem || !selectedId || !mailboxHint || !isActive() || !host.isSelectionCurrent(snapshot))
    {
        return null;
    }

    try
    {
        if (snapshot.isCompose === true)
        {
            const composeType = await host.getComposeTypeAsync(snapshot);
            if (composeType !== 'reply' || !isActive() || !host.isSelectionCurrent(snapshot))
            {
                return null;
            }
        }
        const verifySelectedItem = async (accessToken: string): Promise<IGraphMessage | null> =>
        {
            if (snapshot.isCompose === true || !snapshot.itemId || !isActive() || !host.isSelectionCurrent(snapshot))
            {
                return null;
            }
            let restId: string;
            try
            {
                const mailbox = Office.context.mailbox;
                const hostName = mailbox?.diagnostics?.hostName;
                restId = hostName === 'OutlookIOS' || hostName === 'OutlookAndroid'
                    ? snapshot.itemId : mailbox.convertToRestId(snapshot.itemId, Office.MailboxEnums.RestVersion.v2_0);
            }
            catch
            {
                return null;
            }
            if (!restId || restId === '.' || restId === '..' || restId.length > 2_048
                || !isActive() || !host.isSelectionCurrent(snapshot))
            {
                return null;
            }
            const url = new URL(`${ GRAPH_MESSAGES_PATH }/${ encodeURIComponent(restId) }`, GRAPH_ORIGIN);
            url.searchParams.set('$select', 'id,internetMessageId,conversationId,conversationIndex,receivedDateTime');
            const response = await graphGet(url, accessToken);
            if (!isActive() || !host.isSelectionCurrent(snapshot))
            {
                return null;
            }
            if (!isGraphMessage(response) || response.id !== restId || response.internetMessageId !== selectedId)
            {
                return null;
            }
            return response;
        };
        const token = await acquireToken(mailboxHint, isActive, verifySelectedItem);
        if (!isActive() || !host.isSelectionCurrent(snapshot))
        {
            return null;
        }
        if (!token?.accessToken || !token.username?.trim())
        {
            return null;
        }
        const sameEmail = token.username.toLowerCase() === mailboxHint.toLowerCase();
        const verifiedAlias = !sameEmail && (
            (isOfficeIdentityMailbox() && token.verifiedOfficeIdentity === true)
            || (isOutlookComMailbox() && !!token.verifiedGraphItem)
        );
        if (!sameEmail && !verifiedAlias)
        {
            return null;
        }
        let selected = token.verifiedGraphItem;
        if (selected)
        {
            if (snapshot.isCompose === true || !isGraphMessage(selected) || selected.internetMessageId !== selectedId)
            {
                return null;
            }
        }
        else
        {
            const lookup = await listMessages(`internetMessageId eq '${ selectedId.replace(/'/g, "''") }'`,
                token.accessToken, host, snapshot, isActive);
            if (!lookup || lookup.length !== 1 || lookup[ 0 ].internetMessageId !== selectedId)
            {
                return null;
            }
            selected = lookup[ 0 ];
        }
        if (!selected)
        {
            return null;
        }
        const selectedMessage = selected;
        const cohort = await listMessages(`conversationId eq '${ selectedMessage.conversationId.replace(/'/g, "''") }'`,
            token.accessToken, host, snapshot, isActive);
        if (!cohort || cohort.some((row) => row.conversationId !== selectedMessage.conversationId)
            || cohort.filter((row) => row.id === selectedMessage.id && row.internetMessageId === selectedId).length !== 1
            || cohort.some((row) => row.internetMessageId === selectedId && row.id !== selectedMessage.id)
            || new Set(cohort.map((row) => row.id)).size !== cohort.length)
        {
            return null;
        }

        const referenceRoots = new Set<string>();
        for (const message of cohort)
        {
            if (!isActive() || !host.isSelectionCurrent(snapshot))
            {
                return null;
            }
            const url = new URL(`${ GRAPH_MESSAGES_PATH }/${ encodeURIComponent(message.id) }`, GRAPH_ORIGIN);
            url.searchParams.set('$select', 'internetMessageHeaders');
            const response = await graphGet(url, token.accessToken) as { internetMessageHeaders?: unknown } | null;
            if (!isActive() || !host.isSelectionCurrent(snapshot) || !response)
            {
                return null;
            }
            const headers = response.internetMessageHeaders;
            if (headers === undefined || headers === null)
            {
                continue;
            }
            if (!Array.isArray(headers) || headers.some((header) => !header || typeof header.name !== 'string'
                || typeof header.value !== 'string' || /[\r\n]/.test(header.value)))
            {
                return null;
            }
            const idHeaders = (headers as IGraphHeader[]).filter((header) => ID_HEADERS.has(header.name.toLowerCase()));
            const messageIds = idHeaders.filter((header) => header.name.toLowerCase() === 'message-id');
            if (!messageIds.length)
            {
                if (idHeaders.length)
                {
                    return null;
                }
                continue;
            }
            const messageId = parseOutlookMessageId(message.internetMessageId);
            if (!messageId || messageIds.some((header) => parseOutlookMessageId(header.value) !== messageId))
            {
                return null;
            }
            const identity = resolveOutlookConversationIdentity(messageId,
                idHeaders.map((header) => `${ header.name }: ${ header.value }`).join('\r\n'));
            if (identity.mode !== 'conversation' || !identity.anchorId)
            {
                return null;
            }
            if (identity.reason === 'references')
            {
                referenceRoots.add(identity.anchorId);
                if (referenceRoots.size > 1)
                {
                    return null;
                }
            }
        }
        if (!isActive() || !host.isSelectionCurrent(snapshot))
        {
            return null;
        }
        if (referenceRoots.size === 1)
        {
            return Array.from(referenceRoots)[ 0 ];
        }
        const listedSelected = cohort.find((row) => row.id === selectedMessage.id);
        if (selectedMessage.conversationIndex !== listedSelected?.conversationIndex)
        {
            return null;
        }
        const rootRows: IGraphMessage[] = [];
        for (const row of cohort)
        {
            if (row.conversationIndex == null)
            {
                continue;
            }
            const length = conversationIndexLength(row.conversationIndex);
            if (length === null)
            {
                return null;
            }
            if (length === ROOT_CONVERSATION_INDEX_BYTES)
            {
                rootRows.push(row);
            }
        }
        return isActive() && host.isSelectionCurrent(snapshot) && rootRows.length === 1
            ? parseOutlookMessageId(rootRows[ 0 ].internetMessageId) : null;
    }
    catch
    {
        return null;
    }
}
